import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import * as QPlayerSVGElement from '../QPlayerSVGElement';

interface ComponentProps {
	primaryColor?: string;
	isExpanded?: boolean;
	noBorder?: any;
	isShowResult?: boolean;
	selected?: boolean;
	percent?: string;
	text?: string;
	winner?: string;
	voted?: boolean;
	isMobile?: boolean;
}

const PollPanelWrapper = styled.div<any>`
	top: 40px;
	left: 0;
	right: 0;
	background: #fff;
	padding: 5%;
	border: 1px solid #ced4d8;
	margin: 10px;
	border-radius: 4px;
	font-size: 0.75rem;
	color: #1a273e;
	text-align: left;
	pointer-events: all;
	font-family: inherit;

	${(props) =>
		!props.noShadow &&
		css`
			box-shadow: 3px 3px 3px #00000029;
		`}

	${(props) =>
		props.isChatActivated &&
		css`
			position: absolute;
		`}

	${({ isMobile }) =>
		isMobile &&
		css`
			top: 0;
			padding: 10px 12px;
			position: relative;
			margin: 0;
			border-radius: 0px;
			@media screen and (max-width: 992px) and (orientation: landscape) and (hover: none) and (pointer: coarse) {
				display: flex;
				flex-direction: column-reverse;
			}
		`}
`;

const PollPanelHeader = styled.div<ComponentProps>`
	display: flex;
	color: ${(props) => props.primaryColor ?? '#0C306C'};
	width: 100%;
	justify-content: space-between;
	align-items: center;
	font-size: 0.6875rem;
	cursor: pointer;

	svg:not(:first-child) {
		width: 20px;
		height: 20px;
	}
`;

const PollPanelHeaderTitle = styled.div`
	text-transform: uppercase;
	font-weight: 600;
	cursor: pointer;
	padding: 1px 0;
	display: flex;
	justify-content: flex-start;
	align-items: center;
	svg {
		margin-right: 5px;
	}
`;

const PollQuestion = styled.div<ComponentProps>`
	margin-top: 6px;
	span {
		color: #1a273e99;
	}

	${({ isMobile }) =>
		isMobile &&
		css`
			@media screen and (max-width: 992px) and (orientation: landscape) and (hover: none) and (pointer: coarse) {
				margin-bottom: 6px;
				margin-top: 0px;
			}
		`}
`;

const PollQuestionTitle = styled.div<ComponentProps>`
	${(props) =>
		props.isExpanded !== false &&
		css`
			padding: 8px 0 6px 0;
			margin-bottom: 10px;
			border-bottom: ${props.noBorder ? 'none' : '1px solid #1A273E'};
		`}
`;

const PollOption = styled.div<ComponentProps>`
	display: flex;
	border-radius: 4px;
	justify-content: space-between;
	align-items: center;
	height: 48px;
	margin: 5px 0;
	position: relative;

	${(props) =>
		!(props.isShowResult || props.voted) &&
		css`
			cursor: pointer;
			&:hover {
				background-color: #f2f5f7;
			}
		`}

	${(props) =>
		props.selected && !props.isShowResult
			? css`
					border: 1px solid ${props.primaryColor ?? '#162F68'};
					background-color: #dee9fd;
				`
			: css`
					border: 1px solid #0000004d;
				`}
`;

const PollOptionText = styled.div<ComponentProps>`
	height: 100%;
	width: ${(props) => props.percent};
	border-radius: 4px;

	&:before {
		content: '${(props) => props.text}';
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		justify-content: space-between;
		align-items: center;
		display: flex;
		padding: 0 12px;
	}

	${(props) =>
		props.isShowResult &&
		css`
			background-color: ${props.winner ? '#DBE9FF' : '#F2F5F7'};

			&:after {
				content: '${props.percent}';
				position: absolute;
				top: 0;
				bottom: 0;
				left: 0;
				right: 0;
				justify-content: flex-end;
				align-items: center;
				display: flex;
				padding: 0 12px;
				border-radius: 4px;
			}
		`}
`;

const PollOptionVoted = styled.span<ComponentProps>`
	background: ${(props) => props.primaryColor ?? '#126CFC'};
	color: #ffffff;
	border-radius: 4px;
	font-weight: 600;
	padding: 0 2px;
	margin-right: 12px;
`;

const PollMessage = styled.div`
	margin-top: 8px;
`;

const PollPanelContent = (props: any) => {
	const { t } = useTranslation();

	const { poll, voted, selections, pollResult, onSelectAnswer, isClosed, settings } = props;
	const primaryColor = settings?.playerStyling?.primaryColor ?? '#126cfc';

	const { accountId } = useSelector((state) => (state as any).configurationReducer);

	const onOptionClick = useCallback(
		(e, optionId) => {
			if (voted || isClosed) {
				return;
			}
			onSelectAnswer?.(e, optionId);
		},
		[onSelectAnswer, voted, isClosed]
	);

	const options = useMemo(() => {
		if (!pollResult || !isClosed) {
			return poll?.options ?? [];
		}

		const finalResults = (poll?.options ?? []).map((r: any) => {
			const option = (pollResult?.options ?? []).find((o: any) => r.id === o.id);
			return { ...r, ...option };
		});

		const winner = finalResults.sort((a: any, b: any) => b.result - a.result);
		return finalResults
			.map((option: any) => {
				const percent = Math.floor(!option.result ? 0 : (option.result / (pollResult?.replies ?? 1)) * 100);

				return {
					...option,
					percent: `${percent}%`,
					winner: winner[0].id === option.id,
				};
			})
			.filter(Boolean);
	}, [poll, pollResult, isClosed]);

	const isOptionVoted = useCallback(
		(id) => {
			if (!poll?.id) {
				return false;
			}

			const votedPolls = Cookies.get(`chat_poll_voted_${accountId}`);
			return (votedPolls ?? '').includes(`${poll?.id}-${id}`);
		},
		[accountId, poll?.id]
	);

	return (
		<>
			<PollQuestion isMobile={props.isMobile}>
				<span>{t('POLL_QUESTION')}</span>
				<PollQuestionTitle>{poll?.text}</PollQuestionTitle>
			</PollQuestion>

			{options.length > 0 &&
				options.map &&
				options.map((option: any, index: number) => (
					<PollOption
						primaryColor={primaryColor}
						className="option"
						key={option.id ?? index}
						selected={!isClosed && (isOptionVoted(option.id ?? index) || (selections && selections.includes(option.id ?? index)))}
						isShowResult={pollResult && isClosed}
						onClick={(e) => onOptionClick(e, option.id ?? index)}
						voted={voted || (selections && selections.includes(option.id ?? index))}
					>
						<PollOptionText percent={option.percent} text={option.text} isShowResult={pollResult && isClosed} winner={option.winner} />
						{(isOptionVoted(option.id ?? index) || (selections && selections.includes(option.id ?? index))) && !isClosed && (
							<PollOptionVoted primaryColor={primaryColor}>{t('POLL_VOTED')}</PollOptionVoted>
						)}
					</PollOption>
				))}
		</>
	);
};

const PollPanel = (props: any) => {
	const { t } = useTranslation();

	const { poll, pollResult, isClosed, settings } = props;
	const primaryColor = settings?.playerStyling?.primaryColor ?? '#126cfc';

	if (!poll && !pollResult) {
		return null;
	}

	return (
		<PollPanelWrapper className="poll__wrapper" isChatActivated={true} isMobile={props.isMobile}>
			<PollPanelHeader onClick={props.onTogglePoll} primaryColor={primaryColor === '#126cfc' ? undefined : primaryColor}>
				<PollPanelHeaderTitle>
					<QPlayerSVGElement.Poll color={primaryColor === '#126cfc' ? undefined : primaryColor} />
					{t(pollResult && isClosed ? 'POLL_RESULT' : 'POLL')}
				</PollPanelHeaderTitle>
				{props.isExpanded ? <QPlayerSVGElement.DownIcon /> : <QPlayerSVGElement.UpIcon />}
			</PollPanelHeader>

			{!props.isExpanded && !(props.voted && !props.isClosed) && (
				<PollQuestion isExpanded={false}>
					<PollQuestionTitle isExpanded={false}>{poll?.text}</PollQuestionTitle>
				</PollQuestion>
			)}

			{!props.isExpanded && props.voted && !props.isClosed && <PollMessage>{t('POLL_VOTED_MESSAGE')}</PollMessage>}

			{props.isExpanded && <PollPanelContent {...props} />}
		</PollPanelWrapper>
	);
};

export { PollPanel, PollPanelWrapper, PollPanelHeader, PollPanelHeaderTitle, PollQuestion, PollQuestionTitle, PollMessage };
